import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { ENERGY_UNIT, formatConvertedEnergyValue } from "../../utils/energyUnit";

export const HistoricalRuntimeTooltip = ({
  active,
  payload,
  label,
  valueLabel,
  valuePrefix,
  valueLabelSuffix,
  energyUnit,
  hasSecondaryAxis,
  secondaryValuePrefix,
  lastYearLabel,
}) => {
  if (!active || !payload || !payload.length) return null;

  const currentEntry = payload.find((entry) => entry.dataKey === "energyUsage");
  const lastYearEntry = payload.find((entry) => entry.dataKey === "lastYearEnergyUsage");
  const hasCurrentValue = Number.isFinite(currentEntry?.value);
  const hasLastYearValue = Number.isFinite(lastYearEntry?.value);

  return (
    <div className="rounded-lg px-4 py-3 shadow-md text-xs font-semibold text-white bg-[#1E555C]" data-testid="historical-runtime-tooltip">
      <p>{label}</p>
      {hasCurrentValue ? (
        <p data-testid="historical-runtime-tooltip-current">
          {`${valueLabel}: ${valuePrefix}${formatConvertedEnergyValue(
            currentEntry.value,
            energyUnit
          )}${valueLabelSuffix}${
            hasSecondaryAxis && Number.isFinite(currentEntry?.payload?.energyCost)
              ? ` (${secondaryValuePrefix}${parseFloat(currentEntry.payload.energyCost).toFixed(2)})`
              : ""
          }`}
        </p>
      ) : null}
      {hasLastYearValue ? (
        <p data-testid="historical-runtime-tooltip-last-year">
          {`${lastYearLabel} (${lastYearEntry.payload.lastYearDate}): ${valuePrefix}${formatConvertedEnergyValue(
            lastYearEntry.value,
            energyUnit
          )}${valueLabelSuffix}${
            hasSecondaryAxis && Number.isFinite(lastYearEntry?.payload?.lastYearEnergyCost)
              ? ` (${secondaryValuePrefix}${parseFloat(lastYearEntry.payload.lastYearEnergyCost).toFixed(2)})`
              : ""
          }`}
        </p>
      ) : null}
    </div>
  );
};

const HistoricalRuntime = ({
  data,
  energyUnit = ENERGY_UNIT.KWH,
  energyUnitLabel = "kWh",
  valueLabel = "Energy Usage",
  valuePrefix = "",
  appendEnergyUnitLabel = true,
  axisLabel,
  legendLabel,
  secondaryAxisLabel = null,
  secondaryValuePrefix = "",
  lastYearLabel = null,
  "data-testid": dataTestId = "historical-runtime-chart",
}) => {
  const hasLastYear = Boolean(lastYearLabel);
  const hasSecondaryAxis = Boolean(secondaryAxisLabel);
  const valueLabelSuffix = appendEnergyUnitLabel ? ` ${energyUnitLabel}` : "";
  const resolvedAxisLabel =
    axisLabel ?? `${valueLabel} (in ${valuePrefix ? valuePrefix : energyUnitLabel})`;
  const resolvedLegendLabel =
    legendLabel ?? `${valueLabel} (in ${valuePrefix ? valuePrefix : energyUnitLabel})`;

  const CustomLegend = () => {
    return (
      <div className="flex items-center justify-center gap-2 mt-2">
        <span className="w-3 h-3 border-2 border-blue-500 rounded-full" />
        <span className="text-sm text-[#000000DE] dark:text-white font-medium">
          {resolvedLegendLabel}
        </span>
        {hasLastYear ? (
          <>
            <span className="w-3 h-3 border-2 border-dashed border-[#8A94A6] rounded-full ml-4" />
            <span className="text-sm text-[#000000DE] dark:text-white font-medium" data-testid="historical-runtime-legend-last-year">
              {lastYearLabel}
            </span>
          </>
        ) : null}
      </div>
    );
  };

  return (
    <div className="w-full h-[450px]" data-testid={dataTestId}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{className: "text-[#555] dark:text-white", fontSize: 12, fill: "currentcolor" }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{className: "text-[#555] dark:text-white", fontSize: 12, fill: "currentcolor" }}
            domain={[0, 'auto']}
            tickFormatter={(value) => formatConvertedEnergyValue(value, energyUnit)}
            tickLine={false}
            axisLine={false}
            label={{
              className: "text-[#555] dark:text-white",
              value: resolvedAxisLabel,
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle", fill: "currentcolor", fontSize: 12 },
            }}
          />
          {hasSecondaryAxis ? (
            <YAxis
              yAxisId="right"
              dataKey="energyCost"
              orientation="right"
              tick={{className: "text-[#555] dark:text-white", fontSize: 12, fill: "currentcolor" }}
              domain={[0, "auto"]}
              tickFormatter={(value) => `${secondaryValuePrefix}${Number(value).toFixed(2)}`}
              tickLine={false}
              axisLine={false}
              label={{
                className: "text-[#555] dark:text-white",
                value: secondaryAxisLabel,
                angle: 90,
                position: "insideRight",
                style: { textAnchor: "middle", fill: "currentcolor", fontSize: 12 },
              }}
            />
          ) : null}
          <Tooltip
            content={
              <HistoricalRuntimeTooltip
                valueLabel={valueLabel}
                valuePrefix={valuePrefix}
                valueLabelSuffix={valueLabelSuffix}
                energyUnit={energyUnit}
                hasSecondaryAxis={hasSecondaryAxis}
                secondaryValuePrefix={secondaryValuePrefix}
                lastYearLabel={lastYearLabel}
              />
            }
          />
          <Legend
            verticalAlign="bottom"
            content={<CustomLegend />}
          />
          <Line
            type="monotone"
            dataKey="energyUsage"
            name={resolvedLegendLabel}

            stroke="#1E90FF"
            strokeWidth={2}
            dot={{ r: 4, stroke: "#1E90FF", strokeWidth: 2, fill: "#fff" }}
            activeDot={{ r: 6 }}
            connectNulls
          />
          {hasLastYear ? (
            <Line
              type="monotone"
              dataKey="lastYearEnergyUsage"
              name={lastYearLabel}
              stroke="#8A94A6"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={{ r: 3, stroke: "#8A94A6", strokeWidth: 2, fill: "#fff" }}
              activeDot={{ r: 5 }}
              connectNulls
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>

    </div>
  );
};

export default HistoricalRuntime;
